import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchAgendaProjects } from '../api/projects.api';
import { queryKeys } from '@/shared/lib/queryKeys';
import type { AgendaProject, Id } from '@/shared/types/domain';

/**
 * A leitura reduzida de UM projeto (spec-calendar.md §7.2, decisão 76).
 *
 * O caso que ela existe para resolver: um líder vê no calendário a conferência
 * aprovada de outro ministério, clica, e `projects_select` não devolve aquela
 * linha. Sem este segundo caminho a tela diria "projeto não encontrado" — que é
 * **falso**, e é a pior frase possível ali: a pessoa acabou de vê-lo.
 *
 * Chama a **mesma RPC** do calendário, pela mesma razão de §3.3: a lista de
 * colunas não pode existir em dois lugares.
 *
 * `enabled` é do chamador de propósito — `ProjectDetailPage` só dispara depois
 * de `useProject` voltar `null`. Consultar as duas em paralelo faria toda visita
 * a um projeto do próprio ministério pagar uma RPC que ninguém vai ler.
 */
export function useAgendaProject(
  id: Id | undefined,
  enabled: boolean,
): UseQueryResult<AgendaProject | null> {
  return useQuery({
    queryKey: queryKeys.agendaProject(id ?? ''),
    queryFn: async () => {
      const rows = await fetchAgendaProjects({ projectId: id as Id });
      // A RPC devolve zero linhas tanto para projeto inexistente quanto para
      // projeto fora da filial — e a tela não distingue os dois, pelo mesmo
      // motivo que `ProjectDetailPage` já não distingue hoje: dizer "existe,
      // mas não é seu" entrega a existência.
      return rows[0] ?? null;
    },
    enabled: Boolean(id) && enabled,
    staleTime: 30_000,
  });
}
